/**
 * <tgc-bundle-add> — multi-add bundle UI for the complementary-products block.
 *
 * Wraps a strip of complementary product thumbnails. Each thumb has a small
 * round checkbox in the corner; toggling 1+ reveals an "Add N items to cart"
 * button. Clicking it adds the current product (using whatever variant + qty
 * the live buy form has) plus every checked add-on (qty 1 of each first-
 * available variant) in a single /cart/add.js request, then dispatches a
 * `cart:update` event so Horizon's cart drawer + cart icon refresh.
 *
 * Lives outside Horizon's stock product-form because it adds to the cart
 * from a context (a sibling block) that doesn't own the product form.
 */
(function () {
  if (customElements.get('tgc-bundle-add')) return;

  const CART_UPDATE_EVENT = 'cart:update';
  const CART_ERROR_EVENT = 'cart:error';

  class TgcBundleAdd extends HTMLElement {
    connectedCallback() {
      this.checks = Array.from(this.querySelectorAll('.tgc-comp__check'));
      this.button = this.querySelector('.tgc-comp__bundle-btn');
      this.buttonLabel = this.querySelector('.tgc-comp__bundle-btn-label');
      this.buttonWrap = this.querySelector('.tgc-comp__bundle-btn-wrap');
      this.errorEl = this.querySelector('.tgc-comp__bundle-error');

      this.checks.forEach((c) => c.addEventListener('click', this.handleCheckToggle));
      this.button?.addEventListener('click', this.handleBundleAdd);

      this.update();
    }

    handleCheckToggle = (event) => {
      const btn = event.currentTarget;
      const wasChecked = btn.getAttribute('aria-checked') === 'true';
      btn.setAttribute('aria-checked', wasChecked ? 'false' : 'true');
      btn.closest('.tgc-comp__card')?.classList.toggle('is-selected', !wasChecked);
      this.update();
    };

    update = () => {
      const selectedCount = this.getSelectedItems().length;

      if (!this.buttonWrap) return;

      if (selectedCount === 0) {
        this.buttonWrap.setAttribute('hidden', '');
        return;
      }

      this.buttonWrap.removeAttribute('hidden');

      // total = main product + selected add-ons
      const total = selectedCount + 1;
      if (this.buttonLabel) {
        this.buttonLabel.textContent = `Add ${total} items to cart`;
      }
    };

    getSelectedItems = () => {
      return this.checks
        .filter((c) => c.getAttribute('aria-checked') === 'true')
        .map((c) => ({
          variantId: c.dataset.variantId,
          quantity: 1,
        }))
        .filter((i) => i.variantId);
    };

    /**
     * Read the main product's currently-selected variant id + quantity from
     * the live buy form, so variant changes (and quantity bumps) made via
     * the variant picker / qty input are honored.
     */
    getMainItem = () => {
      const fallbackVariantId = this.dataset.mainVariantId;
      const mainForm = document.querySelector(
        'product-form-component form, form[action*="/cart/add"]'
      );
      const variantId =
        mainForm?.querySelector('input[name="id"]')?.value || fallbackVariantId;
      const qtyInput = mainForm?.querySelector('input[name="quantity"]');
      const quantity = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
      return variantId ? { variantId, quantity } : null;
    };

    handleBundleAdd = async (event) => {
      event.preventDefault();
      if (!this.button || this.button.disabled) return;

      const items = [];
      const main = this.getMainItem();
      if (main) items.push(main);
      items.push(...this.getSelectedItems());

      if (items.length === 0) return;

      this.button.disabled = true;
      this.button.classList.add('is-loading');
      this.errorEl?.setAttribute('hidden', '');

      // Cart-items section IDs so the drawer can morph in the new lines
      const sectionIds = [];
      document.querySelectorAll('cart-items-component').forEach((el) => {
        if (el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
      });

      const payload = {
        items: items.map((i) => ({
          id: Number(i.variantId),
          quantity: i.quantity,
        })),
        sections: sectionIds.join(','),
      };

      const cartAddUrl =
        (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) ||
        '/cart/add.js';

      try {
        const res = await fetch(cartAddUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(payload),
        });
        const data = await res.json();

        if (data.status) {
          // Shopify cart errors return { status, message, description, errors }
          this.showError(data.description || data.message || 'Could not add to cart');
          document.dispatchEvent(
            new CustomEvent(CART_ERROR_EVENT, {
              bubbles: true,
              detail: {
                sourceId: 'tgc-bundle-add',
                data: {
                  message: data.message,
                  description: data.description,
                  errors: data.errors,
                },
              },
            })
          );
          return;
        }

        // Pull the live cart for the drawer's item-count display + listeners
        const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        const cart = cartRes.ok ? await cartRes.json() : null;

        const totalQty = items.reduce((s, i) => s + i.quantity, 0);

        // Match Horizon's CartAddEvent shape so cart-drawer / cart-icon /
        // component-cart-items all refresh as if a normal add happened.
        const updateEvent = new CustomEvent(CART_UPDATE_EVENT, {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: 'tgc-bundle-add',
            data: {
              source: 'tgc-bundle-add',
              itemCount: totalQty,
              sections: data.sections,
            },
          },
        });
        document.dispatchEvent(updateEvent);

        // Reset selections so the strip stays clean post-add
        this.checks.forEach((c) => {
          c.setAttribute('aria-checked', 'false');
          c.closest('.tgc-comp__card')?.classList.remove('is-selected');
        });
        this.update();
      } catch (err) {
        console.error('[tgc-bundle-add]', err);
        this.showError('Could not add to cart. Please try again.');
      } finally {
        this.button.disabled = false;
        this.button.classList.remove('is-loading');
      }
    };

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.removeAttribute('hidden');
      clearTimeout(this._errorTimer);
      this._errorTimer = setTimeout(() => {
        this.errorEl.setAttribute('hidden', '');
      }, 5000);
    }
  }

  customElements.define('tgc-bundle-add', TgcBundleAdd);
})();
